/**
 * Desenhar UM emoji, seja ele unicode ou personalizado.
 *
 * Existe porque há três sítios a fazer a mesma pergunta — o texto de uma
 * mensagem, a bolha de uma reacção e a grelha do seletor — e a resposta tem um
 * ramo que é fácil esquecer: um emoji personalizado guarda-se como o snowflake
 * pelado, sem `<::>` à volta, e desenhá-lo como texto punha `1234567890` no
 * meio da conversa. A distinção faz-se por serem só dígitos: nenhum emoji
 * unicode o é.
 *
 * O ficheiro é só isto de propósito. Ter o ramo escrito uma vez é o que garante
 * que a bolha da reacção e a linha da mensagem nunca discordam sobre o mesmo
 * emoji.
 */

import { useStore } from "../store/store";

/** Um valor de reacção que é só dígitos é o id de um emoji personalizado. */
export const isCustomEmoji = (value: string): boolean => /^\d+$/.test(value);

export function EmojiGlyph({ value }: { value: string }) {
  if (!isCustomEmoji(value)) return <span className="emoji-uni">{value}</span>;
  return <CustomGlyph emojiId={value} />;
}

/**
 * Separado do `EmojiGlyph` porque tem um hook: chamar `useStore` dentro de um
 * componente que também pode devolver cedo é a violação clássica das regras dos
 * hooks — o React contaria hooks diferentes em renders diferentes.
 */
function CustomGlyph({ emojiId }: { emojiId: string }) {
  const emoji = useStore((s) => s.emojis[emojiId]);
  // Apagado, ou de um servidor onde não estamos. Sem o nome (a reacção só
  // guarda o id) o melhor que há para mostrar é um lugar vazio — e é preferível
  // ao id em cru, que ninguém sabe ler.
  if (!emoji) return <span className="emoji-uni emoji-uni--gone" title="Emoji removido" />;
  return <EmojiImage url={emoji.url} name={emoji.name} />;
}

/** A imagem em si. Partilhada com o `RichText`, que já sabe o nome pelo token. */
export function EmojiImage({ url, name }: { url: string; name: string }) {
  return (
    <img className="emoji-inline" src={url} alt={`:${name}:`} title={`:${name}:`} loading="lazy" draggable={false} />
  );
}
